// 구매내역관리(진행추적) — processHistory → 단계 매트릭스 변환 로직
// 순수 함수 모듈. 컴포넌트(ReqPurchaseTracking.vue)와 분리해 단독 검증/재사용 가능.

// 단계 컬럼 정의 (물류 순서). slot:true = 결과 슬롯(여러 status가 한 칸에서 갈림)
export const STAGES = [
  { k: '구매전', g: '구매' }, { k: '협상중', g: '구매' },
  { k: '구매확정', g: '구매', slot: true },
  { k: '입금요청', g: '결제' }, { k: '입금완료', g: '결제' },
  { k: '구매완료', g: '배송' }, { k: '배송중', g: '배송' }, { k: '배송완료', g: '배송' },
  { k: '입고현황', g: '입고', slot: true }, { k: '중국재고', g: '입고', noAging: true },
  { k: '출고현황', g: '출고', slot: true },
  // ⚠ 순서는 서버 WEBHOOK_STATUS_ORDER 를 따른다(2026-08-30 정정).
  //   화주향: 해외입고중 → 국내창고입고중 → 국내창고입고완료 → 국내입고중
  //   직납  : 해외입고중 ─────────────────────────────────→ 국내입고중 (가운데를 건너뛰는 전진)
  //   옛 순서(국내입고중이 앞)로 두면 국내입고중에 도달한 건의 국내창고 칸이 매트릭스에서 사라진다.
  { k: '해외입고중', g: '국내' }, { k: '국내창고입고중', g: '국내' },
  { k: '국내창고입고완료', g: '국내', label: '국내창고재고', link: 'logis.stockmanagement', linkLabel: '재고관리 이동', noAging: true },
  { k: '국내입고중', g: '국내' },
  // 종착점: 입고완료되면 이 추적에서 빠지고 판매현황 SCM(growthScmV0) 소관으로 인계
  { k: '쿠팡센터입고완료', g: '완료', link: 'growth.growthscmV0', linkLabel: '판매관리(SCM) 이동', noAging: true }
]

const STAGE_ORDER = STAGES.map(s => s.k)

// 슬롯 컬럼 ← 실제 processStatus 값 묶음
const SLOT_MAP = {
  '구매확정': ['구매확인', '구매보류', '구매취소'],
  // 재고대기 = 위치번호 넣기 전(아직 재고 아님), 출고확인대기 = 화주 확인 뒤 출고 (2026-10-01)
  '입고현황': ['입고작업', '입고완료', '입고오류', '반품확인', '반품완료', '폐기확인', '폐기완료', '입고대기', '재고대기', '미도착종결'],
  '출고현황': ['출고확인대기', '출고요청', '출고대기', '출고완료']
}

// ── 선적 단위 구간 (정본 §4 "진행현황 칸은 수량 분포다") ─────────────
//
// 구매는 한 번이지만 출고는 여러 번이다. 선적은 출고할 때 비로소 생기므로
// 이 컬럼들부터만 선적 단위로 갈린다. 왼쪽(구매·결제·배송·중국재고)은
// 어느 경우에도 쪼개지지 않는다 — 협상도 결제도 1688 주문도 한 건이기 때문이다.
// ⚠ 서버 WEBHOOK_STATUS_ORDER 와 **같은 순서**여야 한다.
//   해외입고중 → 국내창고입고중 → 국내창고입고완료 → 국내입고중 → 쿠팡센터입고완료
//   여기만 옛 순서로 두면 화주향 선적이 지나온 칸을 미래로, 안 지난 칸을 done 으로 그린다.
export const SHIP_STAGES = ['출고현황', '해외입고중', '국내창고입고중', '국내창고입고완료', '국내입고중', '쿠팡센터입고완료']

// 선적 상태(Shipment.상태) → 매트릭스 컬럼
const SHIP_STATUS_TO_STAGE = {
  '출고완료': '출고현황',
  '해외입고중': '해외입고중',
  '국내창고입고중': '국내창고입고중',
  '국내창고입고완료': '국내창고입고완료',
  '국내입고중': '국내입고중',
  '쿠팡센터입고완료': '쿠팡센터입고완료'
}

// 셀 안에 인라인으로 보여줄 최대 줄 수. 넘으면 "외 N건"으로 접고 모달에서 본다.
// 한 발주가 실제로 몇 번 나뉘어 나가는지 데이터가 없어 잠정값이다 — 써보고 조정한다.
export const SHIP_INLINE_MAX = 3

// 문제/예외 상태 (셀을 빨강 계열로)
const BAD_STATUSES = ['구매보류', '구매취소', '입고오류', '반품확인', '반품완료', '폐기확인', '폐기완료', '입고대기', '미도착종결']

// status → 어느 컬럼인지 역매핑
const STATUS_TO_STAGE = (() => {
  const m = {}
  STAGE_ORDER.forEach(k => { m[k] = k }) // 일반 단계는 자기 자신
  Object.entries(SLOT_MAP).forEach(([stage, list]) => list.forEach(s => { m[s] = stage }))
  return m
})()

// aging 임계값 (일괄 스케일). 나중에 상태별 기준으로 바꿀 때 이 객체만 교체.
export const AGING_THRESHOLDS = { amber: 2, orange: 4, red: 7 }

export function agingLevel(days) {
  if (days >= AGING_THRESHOLDS.red) return 'red'
  if (days >= AGING_THRESHOLDS.orange) return 'orange'
  if (days >= AGING_THRESHOLDS.amber) return 'amber'
  return 'calm'
}

function statusToStage(status) {
  return STATUS_TO_STAGE[status] || status
}

function isSlot(stageKey) {
  return !!SLOT_MAP[stageKey]
}

// changedAt(ISO/Date) 또는 changeDate(number) 폴백 파싱
function parseDate(v) {
  if (v === undefined || v === null) return null
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

function fmtDate(d) {
  if (!d) return ''
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${m}-${day}`
}

// 자정 기준 일수 차이
function durDays(a, b) {
  if (!a || !b) return 0
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate())
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.max(0, Math.round((db - da) / 86400000))
}

/**
 * Reqpurchase 문서 1건 → 매트릭스 행 데이터
 * @param {Object} doc - { _id, 상품명, 옵션1_중국어/옵션2_중국어, 구매요청수량, companyid, 등록이미지, processStatus|진행현황, 요청일, processHistory[] }
 * @param {Date} [now] - 체류일 계산 기준 (기본 오늘)
 */
/**
 * 선적 목록 → 컬럼별 미니줄 배열.
 *
 * 선적 하나가 지금 어느 컬럼에 있는지는 Shipment.상태 가 정하고,
 * 거기까지 오는 동안 지난 컬럼은 Shipment.이력 의 날짜로 채운다.
 *
 * @param {Array} ships /api/reqpurchase/shipmentDistribution 의 선적[reqId]
 * @returns {Object} { [stageKey]: [{ n, qty, st, date, days, lv }] }
 *          st = 'done' | 'cur' | 'none'
 */
export function buildShipCells(ships, now = new Date()) {
  const out = {}
  if (!Array.isArray(ships) || !ships.length) return out
  SHIP_STAGES.forEach(sk => { out[sk] = [] })

  ships.forEach(sp => {
    const curStage = SHIP_STATUS_TO_STAGE[sp.상태] || '출고현황'
    const curIdx = SHIP_STAGES.indexOf(curStage)

    // 단계별 최초 진입시각 (이력이 없으면 출고일로 최소한 출고현황은 채운다)
    const at = {}
    ;(sp.이력 || []).forEach(h => {
      const sk = SHIP_STATUS_TO_STAGE[h.단계]
      const d = parseDate(h.일시)
      if (sk && d && !at[sk]) at[sk] = d
    })
    if (!at['출고현황']) {
      const d = parseDate(sp.출고일)
      if (d) at['출고현황'] = d
    }

    SHIP_STAGES.forEach((sk, idx) => {
      if (idx < curIdx || (idx === curIdx && curIdx === SHIP_STAGES.length - 1)) {
        // 지나온 단계 (마지막 단계에 도달했으면 그것도 완료로 본다)
        //
        // ⚠ 인덱스만 보면 **건너뛴 칸도 지나온 것이 된다.**
        //   직납(destination=coupang)은 해외입고중 → 국내입고중 으로 가운데 둘을 건너뛰는데,
        //   그러면 국내창고입고중·국내창고입고완료가 날짜 없이 ✓ 로 찍혀
        //   "화주창고를 거쳐 갔다" 로 읽힌다.
        //   ⇒ 이력에 그 단계가 실제로 있었을 때만 done. 없으면 'skip'(건너뜀).
        const 지남 = !!at[sk]
        out[sk].push({
          n: sp.n, qty: sp.수량,
          st: 지남 ? 'done' : 'skip',
          date: 지남 ? fmtDate(at[sk]) : '',
          days: 0, lv: 지남 ? 'done' : 'none'
        })
      } else if (idx === curIdx) {
        const days = durDays(at[sk], now)
        out[sk].push({ n: sp.n, qty: sp.수량, st: 'cur', date: fmtDate(at[sk]), days, lv: agingLevel(days) })
      } else {
        // 아직 안 온 단계. 줄은 남겨야 ①②가 칸을 가로질러 정렬된다
        out[sk].push({ n: sp.n, qty: sp.수량, st: 'none', date: '', days: 0, lv: 'none' })
      }
    })
  })
  return out
}

/**
 * @param {Object} doc
 * @param {Date} [now]
 * @param {{ships?:Array, dist?:Object}} [extra] 선적 정보. 없으면 지금까지와 똑같이 그린다.
 */
export function buildRow(doc, now = new Date(), extra) {
  const history = (doc.processHistory || [])
    .map(h => ({ ...h, _at: parseDate(h.changedAt || h.changeDate) }))
    .filter(h => h._at)
    .sort((a, b) => a._at - b._at)

  // 컬럼별 진입정보: 첫 진입시각 유지, 마지막 status/memo 갱신
  const stageInfo = {}
  history.forEach(h => {
    const stage = statusToStage(h.toStatus)
    if (!stageInfo[stage]) {
      stageInfo[stage] = { enterAt: h._at, lastStatus: h.toStatus, memo: h.memo || '' }
    } else {
      stageInfo[stage].lastStatus = h.toStatus
      if (h.memo) stageInfo[stage].memo = h.memo
    }
  })

  const curStatus = doc.processStatus || doc.진행현황 || '구매전'
  const curStage = statusToStage(curStatus)
  const curIdx = STAGE_ORDER.indexOf(curStage)

  // 거친 컬럼들의 진입시각(idx순) — done 단계 dur 계산용
  const visited = STAGE_ORDER
    .map((sk, idx) => ({ sk, idx, at: stageInfo[sk] && stageInfo[sk].enterAt }))
    .filter(v => v.at && v.idx <= curIdx)
    .sort((a, b) => a.idx - b.idx)

  const cells = {}
  visited.forEach((v, i) => {
    const sk = v.sk
    const info = stageInfo[sk]
    if (v.idx < curIdx) {
      // 완료 단계
      const next = visited[i + 1]
      const dur = next ? durDays(info.enterAt, next.at) : 0
      cells[sk] = {
        st: 'done',
        date: fmtDate(info.enterAt),
        dur: dur + '일',
        outcome: isSlot(sk) ? info.lastStatus : undefined,
        bad: BAD_STATUSES.includes(info.lastStatus) || undefined
      }
    } else {
      // 현재 단계 — 재고성 단계(noAging)는 오래 머무는 게 정상이라 색 강조 끔(calm 고정)
      const days = durDays(info.enterAt, now)
      cells[sk] = {
        st: 'cur',
        lv: STAGES[v.idx].noAging ? 'calm' : agingLevel(days),
        date: fmtDate(info.enterAt),
        badge: days + '일째',
        outcome: isSlot(sk) ? curStatus : undefined,
        bad: BAD_STATUSES.includes(curStatus) || undefined
      }
    }
  })

  // 셀 memo = 운영 메모(trackingNotes), 단계당 1개. processHistory 로그는 셀에 표시하지 않음
  const notes = doc.trackingNotes || {}
  Object.keys(cells).forEach(sk => {
    if (notes[sk] && notes[sk].text) cells[sk].memo = notes[sk].text
  })

  // history가 비어 현재 단계 셀이 없으면(이력 누락) 요청일 기준으로 현재 단계 표시
  if (!cells[curStage]) {
    const base = parseDate(doc.요청일) || now
    const days = durDays(base, now)
    const noAging = curIdx >= 0 && STAGES[curIdx].noAging
    cells[curStage] = {
      st: 'cur', lv: noAging ? 'calm' : agingLevel(days), date: fmtDate(base), badge: days + '일째',
      outcome: isSlot(curStage) ? curStatus : undefined,
      bad: BAD_STATUSES.includes(curStatus) || undefined
    }
  }

  // ── 선적이 있으면 물류 구간을 선적별 미니줄로 덮는다 ────────────
  //
  // ⚠ 선적이 없으면 이 블록을 통째로 건너뛴다. 그러면 위에서 만든 셀이 그대로 남고
  //   화면은 지금까지와 완전히 같다(정본 §4 안전 원칙).
  const ships = (extra && extra.ships) || null
  if (ships && ships.length) {
    const shipCells = buildShipCells(ships, now)
    SHIP_STAGES.forEach(sk => {
      const list = shipCells[sk] || []
      if (!list.length) return
      const 진행 = list.filter(x => x.st === 'cur')
      const 완료 = list.filter(x => x.st === 'done')
      // 칸 색은 "제일 오래 머문 선적" 기준. 병목이 그 칸에서 눈에 띄어야 한다
      const worst = 진행.reduce((a, x) => (a && a.days >= x.days ? a : x), null)
      const base = cells[sk] || {}
      cells[sk] = {
        ...base,
        st: 진행.length ? 'cur' : (완료.length ? 'done' : 'future'),
        lv: 진행.length ? (worst ? worst.lv : 'calm') : undefined,
        date: base.date || (worst ? worst.date : ''),
        badge: worst ? worst.days + '일째' : base.badge,
        ships: list
      }
    })
  }

  // 중국재고 칸은 상태가 아니라 잔량이다 — "아직 안 나간 몫"
  const dist = (extra && extra.dist) || null
  if (dist && Array.isArray(dist.줄)) {
    const 잔 = dist.줄.find(r => r.상태 === '중국창고')
    if (잔) cells['중국재고'] = { ...(cells['중국재고'] || {}), st: 'cur', lv: 'calm', qty: 잔.수량 }
  }

  const opt = [doc.옵션1_중국어, doc.옵션2_중국어].filter(Boolean).join(' / ')
  return {
    _id: doc._id,
    p: {
      name: doc.상품명 || '',
      opt,
      meta: `${doc.구매요청수량 || ''}개 · ${doc.companyid || ''}`,
      thumb: doc.등록이미지 || ''
    },
    ships: ships || null,
    dist: dist || null,
    // 쿠패스 창고가 입고 때 핸드폰으로 찍은 사진 [{url, 종류, at}]
    photos: Array.isArray(doc.입고사진) ? doc.입고사진.filter(ph => ph && ph.url) : [],
    cells
  }
}
